import React, { useRef, useState } from "react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/apiClient";
import { UploadCloud, X } from "lucide-react";

export default function FileDrop({ value, onChange, accept = "image/*", label = "Drag & drop an image, or click to browse" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("is_public", "true");
      const { data } = await api.post("/media/upload", fd);
      onChange(fileUrl(data.storage_path || data.file?.storage_path));
      toast.success("Uploaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      {value ? (
        <div className="relative inline-block">
          <img src={value} alt="preview" className="h-28 max-w-full object-contain rounded-md border border-slate-200 bg-white p-2" data-testid="filedrop-preview" />
          <button type="button" onClick={() => onChange("")} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 shadow" data-testid="filedrop-remove"><X size={13} /></button>
        </div>
      ) : (
        <div
          onClick={() => ref.current?.click()}
          tabIndex={0}
          role="button"
          onPaste={(e) => {
            const item = [...(e.clipboardData?.items || [])].find((i) => i.type.startsWith("image/"));
            if (item) { e.preventDefault(); upload(item.getAsFile()); }
          }}
          onDragOver={(e) => { e.preventDefault(); setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => { e.preventDefault(); setOver(false); upload(e.dataTransfer.files?.[0]); }}
          className={`cursor-pointer rounded-md border-2 border-dashed px-6 py-8 text-center transition-colors ${over ? "border-npst-navy bg-npst-goldLight/40" : "border-slate-300 hover:border-npst-navy"}`}
          data-testid="filedrop-zone">
          <UploadCloud size={26} className="mx-auto text-npst-gold mb-2" />
          <p className="text-sm text-slate-500">{busy ? "Uploading…" : label}</p>
        </div>
      )}
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="filedrop-input" />
    </div>
  );
}
